import { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import EquivalenciesVisualizer from "../components/EquivalenciesVisualizer";
import { exportToPDF } from "../utils/pdfExport";
import {GraduationCap, Bus, Sandwich, Lightbulb, Download, Share2, FileText} from 'lucide-react';

export default function StudentCalculator() {
  const [transportation, setTransportation] = useState("bus");
  const [distanceToSchool, setDistanceToSchool] = useState("");
  const [lunchType, setLunchType] = useState("mixed");
  const [homeElectricity, setHomeElectricity] = useState("");
  const [emissions, setEmissions] = useState<number | null>(null);
  const [breakdown, setBreakdown] = useState<{transport: number; lunch: number; home: number} | null>(null);

  const calculateEmissions = () => {
    // Transportation (kg CO2 per mile, 180 school days)
    const distance = parseFloat(distanceToSchool) || 0;
    const transportFactors: Record<string, number> = {
      "walk-bike": 0,
      "bus": 0.089,
      "car": 0.404,
      "carpool": 0.202,
    };
    const transportEmissions = (distance * 2 * transportFactors[transportation] * 180) / 1000;

    // School lunch (tons CO2e per year)
    const lunchFactors: Record<string, number> = {
      "vegetarian": 0.2,
      "mixed": 0.4,
      "meat-heavy": 0.6,
    };
    const lunchEmissions = lunchFactors[lunchType];

    // Home energy (assuming student's portion - 25% of household)
    const electricity = parseFloat(homeElectricity) || 0;
    const homeEmissions = (electricity * 0.92 * 12 * 0.25) / 1000;

    const total = transportEmissions + lunchEmissions + homeEmissions;
    setEmissions(parseFloat(total.toFixed(2)));
    setBreakdown({
      transport: parseFloat(transportEmissions.toFixed(2)),
      lunch: parseFloat(lunchEmissions.toFixed(2)),
      home: parseFloat(homeEmissions.toFixed(2)),
    });
  };

  const shareScore = () => {
    const text = `🎓 My student carbon footprint is ${emissions} tons CO₂e! Calculate yours at www.greenhousegascalculator.com/student`;
    if (navigator.share) {
      navigator.share({ title: "Student Carbon Footprint", text });
    } else {
      navigator.clipboard.writeText(text);
      alert("Score copied! Share with your class! 🌍");
    }
  };

  const downloadWorksheet = () => {
    const worksheetText = `STUDENT CARBON FOOTPRINT WORKSHEET
For Ages 10-15

Name: _______________________     Date: _____________

PART 1: WHAT IS THE GREENHOUSE EFFECT?

The greenhouse effect is like a blanket around Earth! When sunlight hits Earth, some heat escapes back into space. But greenhouse gases (like CO₂) trap some of that heat, keeping Earth warm enough for life.

🌍 Draw a simple diagram:
[Space for drawing]

Without greenhouse gases: Too cold! ❄️
With some greenhouse gases: Just right! 🌱
With too many greenhouse gases: Too hot! 🔥

KEY TERMS:
• Carbon Dioxide (CO₂): A gas we breathe out and cars produce
• Carbon Footprint: How much CO₂ your activities create
• Emissions: CO₂ released into the air

PART 2: CALCULATE YOUR CARBON FOOTPRINT

1. How do you get to school?
   ⃝ Walk/Bike (0 emissions!)
   ⃝ School Bus
   ⃝ Car
   
   Distance to school: ______ miles

2. What do you usually eat for lunch?
   ⃝ Vegetarian (lower emissions)
   ⃝ Mixed foods
   ⃝ Meat every day (higher emissions)

3. Home Energy
   Ask your parents: How many kWh does your home use per month?
   Your family's usage: ______ kWh
   Your portion (÷4): ______ kWh

YOUR RESULTS:
Transportation: ______ tons CO₂e/year
Food: ______ tons CO₂e/year  
Home Energy: ______ tons CO₂e/year
TOTAL: ______ tons CO₂e/year

PART 3: EQUIVALENCIES - WHAT DOES THIS MEAN?

Your footprint = ______ trees needed to absorb your CO₂
Your footprint = ______ miles driven in a car
Your footprint = ______ smartphones charged

PART 4: ACTION PLAN!

What can YOU do to reduce your carbon footprint?

3 things I can start doing TODAY:
1. _________________________________________________
2. _________________________________________________
3. _________________________________________________

Ideas:
✅ Walk or bike to school when possible
✅ Turn off lights when leaving a room
✅ Eat more vegetables (they're good for you AND the planet!)
✅ Bring reusable water bottles and lunch boxes
✅ Plant a tree with your family
✅ Teach your friends about carbon footprints!

TEACHER NOTES:
This worksheet aligns with NGSS standards for climate science education (grades 5-8).

For more information: www.greenhousegascalculator.com/science
`;

    const blob = new Blob([worksheetText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Student_Carbon_Footprint_Worksheet.txt';
    link.click();
    URL.revokeObjectURL(url);
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Carbon Footprint Calculator for Students",
    "url": "https://www.greenhousegascalculator.com/student",
    "description": "Educational carbon footprint calculator for students ages 10-15. Learn about greenhouse effect and climate science with simple, fun tools.",
    "applicationCategory": "EducationalApplication",
    "educationalLevel": "Middle School",
  };

  return (
    <div className="bg-gradient-to-b from-yellow-50 via-green-50 to-blue-50">
      <SEO
        title="Carbon Footprint Calculator for Students | Kids Climate Education"
        description="Free carbon footprint calculator for students and kids ages 10-15. Learn about greenhouse effect, climate science, and how to reduce emissions. Includes teacher worksheet!"
        keywords="carbon footprint calculator for students, kids climate calculator, student emissions calculator, greenhouse effect for kids, climate education"
        canonical="https://www.greenhousegascalculator.com/student"
        schema={schema}
      />

      {/* Hero - Colorful and Kid-Friendly */}
      <section className="py-16 bg-gradient-to-r from-yellow-400 via-green-400 to-blue-400">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <div className="flex justify-center mb-4">
              <GraduationCap className="w-20 h-20" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              🌍 Student Carbon Footprint Calculator
            </h1>
            <p className="text-2xl mb-2">
              Learn How YOU Can Help Planet Earth!
            </p>
            <p className="text-xl opacity-90">
              (Ages 10-15)
            </p>
          </div>
        </div>
      </section>

      {/* Simple Explanation Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-yellow-100 border-4 border-yellow-400 rounded-2xl p-8 mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
              🤔 What is a Carbon Footprint?
            </h2>
            <p className="text-lg text-gray-800 mb-4">
              Your <strong>carbon footprint</strong> is the amount of carbon dioxide (CO₂) that your activities create. CO₂ is a gas that makes Earth warmer - kind of like putting a blanket over the planet!
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="bg-white p-4 rounded-lg text-center">
                <Bus className="w-12 h-12 text-blue-600 mx-auto mb-2" />
                <p className="font-semibold text-gray-900">🚌 Transportation</p>
                <p className="text-sm text-gray-600">How you get to school</p>
              </div>
              <div className="bg-white p-4 rounded-lg text-center">
                <Sandwich className="w-12 h-12 text-green-600 mx-auto mb-2" />
                <p className="font-semibold text-gray-900">🍎 Food</p>
                <p className="text-sm text-gray-600">What you eat</p>
              </div>
              <div className="bg-white p-4 rounded-lg text-center">
                <Lightbulb className="w-12 h-12 text-yellow-600 mx-auto mb-2" />
                <p className="font-semibold text-gray-900">💡 Energy</p>
                <p className="text-sm text-gray-600">Electricity at home</p>
              </div>
            </div>
          </div>

          {/* Greenhouse Effect Explanation */}
          <div className="bg-blue-100 border-4 border-blue-400 rounded-2xl p-8 mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
              🏠 What is the Greenhouse Effect?
            </h2>
            <p className="text-lg text-gray-800 mb-4">
              The <strong>greenhouse effect</strong> is what keeps Earth warm! Here's how it works:
            </p>
            <div className="space-y-3 text-gray-800">
              <div className="bg-white p-4 rounded-lg">
                <p className="font-semibold">1. ☀️ Sunlight hits Earth and warms it up</p>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <p className="font-semibold">2. 🌡️ Earth releases heat back into space</p>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <p className="font-semibold">3. 🌫️ Greenhouse gases trap some of that heat (like a blanket!)</p>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <p className="font-semibold">4. 🔥 Too much CO₂ = Too much heat = Earth gets too warm!</p>
              </div>
            </div>
            <div className="mt-6 bg-green-50 p-4 rounded-lg">
              <p className="text-gray-800">
                <strong>🌱 The Good News:</strong> YOU can help by reducing your carbon footprint! Every little action counts!
              </p>
            </div>
            <div className="mt-4 text-center">
              <Link
                to="/science"
                className="inline-flex items-center gap-2 bg-blue-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors"
              >
                🔬 Learn More Science Here!
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator - Simplified for Kids */}
      <section className="py-16 bg-gradient-to-b from-green-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 shadow-2xl border-4 border-[#2E8B57]">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              📊 Calculate YOUR Carbon Footprint!
            </h2>

            <div className="space-y-6 mb-8">
              <div className="bg-blue-50 p-6 rounded-xl border-2 border-blue-300">
                <div className="flex items-center gap-2 mb-4">
                  <Bus className="w-6 h-6 text-[#2E8B57]" />
                  <label className="font-bold text-gray-900 text-lg">🚌 How do you get to school?</label>
                </div>
                <select
                  value={transportation}
                  onChange={(e) => setTransportation(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-[#2E8B57]"
                >
                  <option value="walk-bike">🚶 Walk or Bike (Best for Earth!)</option>
                  <option value="bus">🚌 School Bus</option>
                  <option value="carpool">🚗 Carpool with Friends</option>
                  <option value="car">🚙 Car (Just Me & Parent)</option>
                </select>
                <div className="mt-4">
                  <label className="text-gray-700 font-semibold block mb-2">Distance to school (miles):</label>
                  <input
                    type="number"
                    value={distanceToSchool}
                    onChange={(e) => setDistanceToSchool(e.target.value)}
                    placeholder="e.g., 5"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-[#2E8B57]"
                  />
                </div>
              </div>

              <div className="bg-green-50 p-6 rounded-xl border-2 border-green-300">
                <div className="flex items-center gap-2 mb-4">
                  <Sandwich className="w-6 h-6 text-[#2E8B57]" />
                  <label className="font-bold text-gray-900 text-lg">🍎 What do you usually eat for lunch?</label>
                </div>
                <select
                  value={lunchType}
                  onChange={(e) => setLunchType(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-[#2E8B57]"
                >
                  <option value="vegetarian">🥗 Vegetarian (Lower Carbon!)</option>
                  <option value="mixed">🍔 Mix of Everything</option>
                  <option value="meat-heavy">🍖 Meat Every Day</option>
                </select>
                <p className="text-sm text-gray-600 mt-2">
                  💡 <strong>Fun Fact:</strong> Vegetables have a smaller carbon footprint than meat!
                </p>
              </div>

              <div className="bg-yellow-50 p-6 rounded-xl border-2 border-yellow-300">
                <div className="flex items-center gap-2 mb-4">
                  <Lightbulb className="w-6 h-6 text-[#2E8B57]" />
                  <label className="font-bold text-gray-900 text-lg">💡 Home Electricity</label>
                </div>
                <p className="text-gray-700 mb-3">
                  Ask your parents: How much electricity does your home use per month? (Look at the electric bill!)
                </p>
                <input
                  type="number"
                  value={homeElectricity}
                  onChange={(e) => setHomeElectricity(e.target.value)}
                  placeholder="e.g., 900 kWh"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-[#2E8B57]"
                />
                <p className="text-xs text-gray-500 mt-2">
                  We'll calculate your share (about 1/4 of your family's usage)
                </p>
              </div>
            </div>

            <button
              onClick={calculateEmissions}
              className="w-full bg-gradient-to-r from-green-500 to-blue-500 text-white py-4 rounded-xl font-bold text-xl hover:from-green-600 hover:to-blue-600 transition-all transform hover:scale-105 shadow-lg"
            >
              🌍 Calculate My Footprint!
            </button>

            {emissions !== null && breakdown && (
              <div className="mt-8 space-y-4">
                <div className="bg-gradient-to-r from-yellow-100 to-green-100 p-8 rounded-2xl border-4 border-[#2E8B57]">
                  <div className="text-center">
                    <p className="text-xl text-gray-700 mb-2 font-semibold">🎉 Your Annual Carbon Footprint:</p>
                    <p className="text-5xl font-bold text-[#2E8B57] mb-2">
                      {emissions} tons
                    </p>
                    <p className="text-lg text-gray-600">of CO₂ per year</p>
                    <p className="text-lg text-gray-800 mt-4">
                      {emissions < 1.5 ? "🌟 WOW! You're doing GREAT!" : 
                       emissions < 3 ? "✅ Good job! Keep it up!" :
                       "💪 You can do even better! Try the tips below!"}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl border-2 border-gray-300">
                  <h3 className="font-bold text-gray-900 mb-4 text-xl">📊 Where Does It Come From?</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center bg-white p-3 rounded-lg">
                      <span className="text-gray-700 font-semibold">🚌 Getting to School</span>
                      <span className="font-bold text-blue-600">{breakdown.transport} tons</span>
                    </div>
                    <div className="flex justify-between items-center bg-white p-3 rounded-lg">
                      <span className="text-gray-700 font-semibold">🍎 Food</span>
                      <span className="font-bold text-green-600">{breakdown.lunch} tons</span>
                    </div>
                    <div className="flex justify-between items-center bg-white p-3 rounded-lg">
                      <span className="text-gray-700 font-semibold">💡 Home Energy</span>
                      <span className="font-bold text-yellow-600">{breakdown.home} tons</span>
                    </div>
                  </div>
                </div>

                <EquivalenciesVisualizer emissions={emissions} />

                <div className="bg-blue-50 border-4 border-blue-400 p-6 rounded-xl">
                  <h3 className="font-bold text-gray-900 mb-4 text-xl">🌱 How Can YOU Help?</h3>
                  <ul className="space-y-2 text-gray-800">
                    <li>✅ Walk or bike to school when you can</li>
                    <li>✅ Turn off lights when you leave a room</li>
                    <li>✅ Eat more veggies (they're tasty AND good for Earth!)</li>
                    <li>✅ Unplug chargers when not using them</li>
                    <li>✅ Use reusable water bottles and lunch boxes</li>
                    <li>✅ Teach your friends about carbon footprints!</li>
                  </ul>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={() => exportToPDF({
                      title: "Student Carbon Footprint Report",
                      totalEmissions: emissions,
                      breakdown,
                      calculationType: "student"
                    })}
                    className="bg-[#2E8B57] text-white px-6 py-4 rounded-lg font-bold hover:bg-[#267347] transition-colors flex items-center justify-center gap-2 text-lg"
                  >
                    <Download className="w-6 h-6" />
                    Download My Report
                  </button>
                  <button
                    onClick={shareScore}
                    className="bg-blue-500 text-white px-6 py-4 rounded-lg font-bold hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 text-lg"
                  >
                    <Share2 className="w-6 h-6" />
                    Share with Friends!
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Teacher's Guide Section */}
      <section className="py-16 bg-gradient-to-b from-purple-50 to-pink-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 shadow-xl border-4 border-purple-400">
            <div className="text-center mb-6">
              <FileText className="w-16 h-16 text-purple-600 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                📚 Teacher's Guide & Classroom Resources
              </h2>
              <p className="text-lg text-gray-700">
                Free downloadable worksheet for ages 10-15
              </p>
            </div>

            <div className="bg-purple-50 p-6 rounded-xl mb-6">
              <h3 className="font-bold text-gray-900 text-xl mb-3">What's Included:</h3>
              <ul className="space-y-2 text-gray-800">
                <li>✅ Simple explanation of the Greenhouse Effect</li>
                <li>✅ Step-by-step carbon footprint calculation</li>
                <li>✅ Real-world equivalencies (trees, miles driven)</li>
                <li>✅ Student action plan template</li>
                <li>✅ Aligned with NGSS climate science standards (grades 5-8)</li>
              </ul>
            </div>

            <button
              onClick={downloadWorksheet}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-4 rounded-xl font-bold text-xl hover:from-purple-600 hover:to-pink-600 transition-all transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
            >
              <Download className="w-6 h-6" />
              Download FREE Student Worksheet
            </button>

            <p className="text-center text-sm text-gray-600 mt-4">
              Perfect for science class, environmental clubs, or Earth Day activities!
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
